import { LazyQueryExecFunction } from '@apollo/client';
import Big from 'big.js';
import { cloneDeep } from 'lodash';

import { Deal } from '../gql/dealGql';
import {
  GetFeesQuery,
  GetFeesQueryVariables,
  TtGetFeesSourceType,
  useGetFeesLazyQuery,
} from '../gql/generated/graphql';

import { isDeal, mapDealToGetTTFeesVariables } from '../utils/deals';
import { isDoubleTaxApplicable } from '../utils/payoffs';
import {
  TemporaryDataWithTotalPayoff,
  mapTemporaryDataToGetTTFeesVariables,
} from '../utils/temporaryData';

import { useLazyLienholder } from './useLazyLienholder';

type GetVehiclePayoffAndSalesTaxFromPayoffArgs = {
  userEnteredTotalPayoff?: number;
  salesTaxFromPayoff?: number;
  salesTaxFromPayoffEnteredManually?: boolean;
  payoffIncludesSalesTax?: boolean;
  buyerNotLessee?: boolean;
  doubleTax?: boolean;
  getTTFees: LazyQueryExecFunction<GetFeesQuery, GetFeesQueryVariables>;
  getTTFeesVariables: GetFeesQueryVariables;
};

type Result = {
  vehiclePayoff?: number;
  salesTaxFromPayoff?: number;
  doubleTaxApplied?: boolean;
  totalPayoffWithoutSalesTax?: number;
};

const getVehiclePayoffAndSalesTaxFromPayoff = async ({
  userEnteredTotalPayoff = 0,
  salesTaxFromPayoff = 0,
  salesTaxFromPayoffEnteredManually,
  payoffIncludesSalesTax,
  buyerNotLessee,
  doubleTax,
  getTTFees,
  getTTFeesVariables: variables,
}: GetVehiclePayoffAndSalesTaxFromPayoffArgs): Promise<Result> => {
  if (!payoffIncludesSalesTax) {
    return {
      vehiclePayoff: userEnteredTotalPayoff,
      salesTaxFromPayoff: 0,
      doubleTaxApplied: false,
      totalPayoffWithoutSalesTax: userEnteredTotalPayoff,
    };
  }

  // This is controlling the adding of double sales tax.
  // It does *not* control the double sales tax warning in ../components/DealIssuesModal/index.tsx
  const doubleTaxApplicable = isDoubleTaxApplicable(buyerNotLessee, doubleTax);

  if (salesTaxFromPayoffEnteredManually) {
    const currentVehiclePayoff = new Big(userEnteredTotalPayoff)
      .minus(salesTaxFromPayoff ?? 0)
      .round(2)
      .toNumber();

    return {
      vehiclePayoff: doubleTaxApplicable ? userEnteredTotalPayoff : currentVehiclePayoff,
      salesTaxFromPayoff,
      doubleTaxApplied: doubleTaxApplicable,
      totalPayoffWithoutSalesTax: currentVehiclePayoff,
    };
  }

  const result = await getTTFees({ variables });
  // We need to calculate `salesTaxFromPayoff` using the `salesTaxRate` instead setting `baseTaxAmount` directly.
  // This is because the `baseTaxAmount` is calculated based on the previous `vehicle_payoff` when getting the fees.
  const salesTaxRate = result?.data?.getFees?.salesTaxRate ?? 0;
  const denominator = new Big(salesTaxRate).plus(1);

  const totalPayoffWithoutSalesTax = new Big(userEnteredTotalPayoff)
    .div(denominator)
    .round(2)
    .toNumber();
  const newVehiclePayoff = doubleTaxApplicable
    ? userEnteredTotalPayoff
    : totalPayoffWithoutSalesTax;
  const newSalesTaxFromPayoff = new Big(userEnteredTotalPayoff)
    .minus(totalPayoffWithoutSalesTax)
    .round(2)
    .toNumber();

  return {
    vehiclePayoff: newVehiclePayoff,
    salesTaxFromPayoff: newSalesTaxFromPayoff,
    doubleTaxApplied: doubleTaxApplicable,
    totalPayoffWithoutSalesTax,
  };
};

type BasePayoffValuesParams = {
  getTTFees: LazyQueryExecFunction<GetFeesQuery, GetFeesQueryVariables>;
  doubleTax?: boolean;
  userEnteredTotalPayoff?: number;
};

type DealPayoffValuesParams = BasePayoffValuesParams & {
  data: Deal;
};

type TemporaryDataPayoffValuesParams = BasePayoffValuesParams & {
  data: TemporaryDataWithTotalPayoff;
};

const getUpdatedDealPayoffValues = async ({
  data,
  getTTFees,
  doubleTax,
  userEnteredTotalPayoff,
}: DealPayoffValuesParams): Promise<Result> => {
  const newDeal = cloneDeep(data);

  const getTTFeesVariables = mapDealToGetTTFeesVariables(newDeal, {
    ttEndpoint: 'getFees',
    ttGetFeesSource: TtGetFeesSourceType.DdPayoffTax,
  });
  const payoffValues = await getVehiclePayoffAndSalesTaxFromPayoff({
    userEnteredTotalPayoff,
    salesTaxFromPayoff: newDeal.car.payoff.sales_tax_from_payoff,
    salesTaxFromPayoffEnteredManually: newDeal.car.payoff.sales_tax_from_payoff_entered_manually,
    payoffIncludesSalesTax: newDeal.car.payoff.payoff_includes_sales_tax,
    buyerNotLessee: newDeal.financial_info?.buyer_not_lessee,
    doubleTax,
    getTTFees,
    // We don't send `financialInfoId` here because we don't want to save an invalid `baseTaxAmount` (it's calculated based on the previous `vehicle_payoff`).
    getTTFeesVariables,
  });

  return payoffValues;
};

const getUpdatedTemporaryDataPayoffValues = async ({
  data,
  getTTFees,
  doubleTax,
  userEnteredTotalPayoff,
}: TemporaryDataPayoffValuesParams): Promise<Result> => {
  const newTemporaryData = cloneDeep(data);

  const getTTFeesVariables = mapTemporaryDataToGetTTFeesVariables(newTemporaryData);
  const payoffValues = await getVehiclePayoffAndSalesTaxFromPayoff({
    userEnteredTotalPayoff,
    salesTaxFromPayoff: newTemporaryData.sales_tax_from_payoff ?? 0,
    salesTaxFromPayoffEnteredManually: !!newTemporaryData.sales_tax_from_payoff_entered_manually,
    payoffIncludesSalesTax: !!newTemporaryData.payoff_includes_sales_tax,
    // TODO: buyerNotLessee is not available in COM so double tax is not applicable at this point
    buyerNotLessee: false,
    doubleTax,
    getTTFees,
    getTTFeesVariables,
  });

  return payoffValues;
};

interface Parameters {
  data: Deal | TemporaryDataWithTotalPayoff;
  userEnteredTotalPayoff?: number;
}

export const useGetUpdatedPayoffValues = () => {
  const [getTTFees] = useGetFeesLazyQuery({
    fetchPolicy: 'cache-first',
  });
  const { getLienholder } = useLazyLienholder();

  const getUpdatedPayoffValues = async ({
    data,
    userEnteredTotalPayoff,
  }: Parameters): Promise<Result> => {
    const { doubleTax } = await getLienholder(data);

    if (isDeal(data)) {
      return getUpdatedDealPayoffValues({
        data,
        getTTFees,
        doubleTax,
        userEnteredTotalPayoff,
      });
    }

    return getUpdatedTemporaryDataPayoffValues({
      data,
      getTTFees,
      doubleTax,
      userEnteredTotalPayoff,
    });
  };

  return { getUpdatedPayoffValues };
};
